import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "@tanstack/react-router"
import { useState } from "react"

import {
  type Body_login_login_access_token as AccessToken,
  type ApiError,
  LoginService,
  type SwitchOrganizationRequest,
  type UserPublic,
  UsersService,
} from "@/client"
import useCustomToast from "@/hooks/useCustomToast"
import { handleError } from "@/utils"

const isLoggedIn = () => {
  return (
    localStorage.getItem("access_token") !== null ||
    sessionStorage.getItem("access_token") !== null
  )
}

type SignupPayload = {
  full_name?: string | null
  email: string
  password: string
}

const useAuth = () => {
  const [error, setError] = useState<string | null>(null)
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { showErrorToast } = useCustomToast()
  const { data: user } = useQuery<UserPublic | null, Error>({
    queryKey: ["currentUser"],
    queryFn: UsersService.readUserMe,
    enabled: isLoggedIn(),
  })

  const signUpMutation = useMutation({
    mutationFn: async (_data: SignupPayload) => {
      throw new Error(
        "Self-service signup is currently disabled. Please contact your administrator.",
      )
    },
    onError: (err: Error) => {
      showErrorToast(err.message)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] })
    },
  })

  const login = async (data: AccessToken) => {
    const response = await LoginService.loginAccessToken({
      formData: data,
    })
    localStorage.setItem("access_token", response.access_token)

    // Check if user has a preferred org from previous session
    const preferredOrgId = localStorage.getItem("selected_org_id")
    if (preferredOrgId) {
      try {
        // Try to switch to the preferred org
        const switchResponse = await LoginService.switchOrganization({
          requestBody: { org_id: preferredOrgId } as SwitchOrganizationRequest,
        })
        // Update token with the preferred org
        localStorage.setItem("access_token", switchResponse.access_token)
      } catch {
        // If switch fails (user no longer member), clear the preference
        localStorage.removeItem("selected_org_id")
      }
    }
  }

  const loginMutation = useMutation({
    mutationFn: login,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["currentUser"] })
      navigate({ to: "/" })
    },
    onError: (err: ApiError) => {
      handleError(err)
    },
  })

  const logout = () => {
    const tokenKeys = ["access_token", "token"]

    for (const key of tokenKeys) {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    }

    // Note: We keep selected_org_id to restore user's preferred org on re-login

    queryClient.clear() // Clear all cached queries
    navigate({ to: "/login" })
  }

  return {
    signUpMutation,
    loginMutation,
    logout,
    user,
    error,
    resetError: () => setError(null),
  }
}

export { isLoggedIn }
export default useAuth
